// src/components/settings/SchedulingTab.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { schedulingAPI } from '../../services/api';
import {
    ClockIcon,
    PlayIcon,
    PauseIcon,
    PlusIcon,
    TrashIcon,
    PencilIcon,
    CalendarIcon,
    ChartBarIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    Cog6ToothIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import ScheduleForm from './ScheduleForm';

function SchedulingTab() {
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const queryClient = useQueryClient();

    // Fetch user's schedules
    const { data: schedules, isLoading } = useQuery('schedules', schedulingAPI.getSchedules);

    // Fetch plan limits
    const { data: planLimits } = useQuery('planLimits', schedulingAPI.getPlanLimits);

    // Mutations
    const pauseMutation = useMutation(schedulingAPI.pauseSchedule, {
        onSuccess: () => {
            queryClient.invalidateQueries('schedules');
            toast.success('Schedule paused');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to pause schedule');
        }
    });

    const resumeMutation = useMutation(schedulingAPI.resumeSchedule, {
        onSuccess: () => {
            queryClient.invalidateQueries('schedules');
            toast.success('Schedule resumed');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to resume schedule');
        }
    });

    const deleteMutation = useMutation(schedulingAPI.deleteSchedule, {
        onSuccess: () => {
            queryClient.invalidateQueries('schedules');
            toast.success('Schedule deleted');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete schedule');
        }
    });

    const triggerMutation = useMutation(schedulingAPI.triggerSchedule, {
        onSuccess: () => {
            queryClient.invalidateQueries('schedules');
            toast.success('Schedule triggered manually');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to trigger schedule');
        }
    });

    const handlePauseResume = (schedule) => {
        if (schedule.isPaused) {
            resumeMutation.mutate(schedule._id);
        } else {
            pauseMutation.mutate(schedule._id);
        }
    };

    const handleDelete = (schedule) => {
        if (window.confirm(`Are you sure you want to delete the schedule "${schedule.name}"?`)) {
            deleteMutation.mutate(schedule._id);
        }
    };

    const handleEdit = (schedule) => {
        setEditingSchedule(schedule);
        setShowCreateForm(true);
    };

    const handleTrigger = (schedule) => {
        if (window.confirm(`Manually trigger the schedule "${schedule.name}" now?`)) {
            triggerMutation.mutate(schedule._id);
        }
    };

    const handleFormClose = () => {
        setShowCreateForm(false);
        setEditingSchedule(null);
    };

    const getScheduleStatus = (schedule) => {
        if (!schedule.isActive) return { text: 'Inactive', color: 'text-gray-500 bg-gray-100' };
        if (schedule.isPaused) return { text: 'Paused', color: 'text-yellow-600 bg-yellow-100' };
        return { text: 'Active', color: 'text-green-600 bg-green-100' };
    };

    const formatNextRun = (nextRun) => {
        if (!nextRun) return 'Not scheduled';
        const date = new Date(nextRun);
        const now = new Date();
        const diffMs = date - now;

        if (diffMs < 0) return 'Overdue';

        const diffMinutes = Math.round(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMinutes / 60);
        const remainingMinutes = diffMinutes % 60;

        // Less than 1 hour - show minutes only
        if (diffHours < 1) {
            return `In ${diffMinutes}m`;
        }

        // Less than 24 hours - show hours and minutes
        if (diffHours < 24) {
            if (remainingMinutes === 0) {
                return `In ${diffHours}h`;
            }
            return `In ${diffHours}h ${remainingMinutes}m`;
        }

        // 24+ hours - show days
        const diffDays = Math.round(diffHours / 24);
        return `In ${diffDays}d`;
    };

    const canCreateSchedule = () => {
        const userSchedules = schedules?.data?.data || [];
        const limits = planLimits?.data?.data || { maxSchedules: 1 };
        return userSchedules.length < limits.maxSchedules;
    };

    if (isLoading) {
        return (
            <div className="animate-pulse space-y-4">
                <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                <div className="h-32 bg-gray-200 rounded"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
            </div>
        );
    }

    const userSchedules = schedules?.data?.data || [];
    const limits = planLimits?.data?.data || {};

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-gray-900">Post Scheduling</h3>
                <p className="mt-1 text-sm text-gray-600">
                    Automate your content generation with scheduled posts
                </p>
            </div>

            {/* Plan Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                            Current Plan Limits
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <span className="font-medium">Schedules:</span> {userSchedules.length}/{limits.maxSchedules}
                                </div>
                                <div>
                                    <span className="font-medium">Posts per day:</span> Up to {limits.maxPostsPerDay}
                                </div>
                            </div>
                            <p className="mt-2">{limits.description}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Create Schedule Button */}
            <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                    <h4 className="text-md font-medium text-gray-900">
                        Active Schedules ({userSchedules.length})
                    </h4>
                </div>

                <button
                    onClick={() => setShowCreateForm(true)}
                    disabled={!canCreateSchedule()}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    {canCreateSchedule() ? 'Create Schedule' : 'Plan Limit Reached'}
                </button>
            </div>

            {/* Schedules List */}
            {userSchedules.length === 0 ? (
                <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
                    <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No schedules configured</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Create your first schedule to automate post generation
                    </p>
                    {canCreateSchedule() && (
                        <div className="mt-6">
                            <button
                                onClick={() => setShowCreateForm(true)}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                            >
                                <PlusIcon className="h-4 w-4 mr-2" />
                                Create First Schedule
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {userSchedules.map((schedule) => {
                        const status = getScheduleStatus(schedule);

                        return (
                            <div key={schedule._id} className="bg-white border border-gray-200 rounded-lg p-6">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <h5 className="text-lg font-medium text-gray-900">
                                                {schedule.name}
                                            </h5>
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                                                {status.text}
                                            </span>
                                        </div>

                                        {schedule.description && (
                                            <p className="text-sm text-gray-600 mb-3">
                                                {schedule.description}
                                            </p>
                                        )}

                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                            <div>
                                                <span className="text-gray-500">Frequency:</span>
                                                <div className="font-medium">{schedule.schedule.description}</div>
                                            </div>

                                            <div>
                                                <span className="text-gray-500">Next Run:</span>
                                                <div className="font-medium">
                                                    {formatNextRun(schedule.schedule.nextRun)}
                                                </div>
                                            </div>

                                            <div>
                                                <span className="text-gray-500">Success Rate:</span>
                                                <div className="font-medium">
                                                    {schedule.statistics.totalRuns > 0
                                                        ? Math.round((schedule.statistics.successfulRuns / schedule.statistics.totalRuns) * 100)
                                                        : 0}%
                                                </div>
                                            </div>

                                            <div>
                                                <span className="text-gray-500">Posts Today:</span>
                                                <div className="font-medium">
                                                    {schedule.limits.postsGeneratedToday}/{schedule.limits.maxPostsPerDay}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Platforms */}
                                        {schedule.generationSettings.platforms.length > 0 && (
                                            <div className="mt-3">
                                                <span className="text-sm text-gray-500">Platforms: </span>
                                                <div className="inline-flex flex-wrap gap-1 mt-1">
                                                    {schedule.generationSettings.platforms.map((platform) => (
                                                        <span
                                                            key={platform}
                                                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                                                        >
                                                            {platform}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Error Display */}
                                        {schedule.statistics.lastError && (
                                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                                                <div className="flex">
                                                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                                                    <div className="ml-3">
                                                        <h3 className="text-sm font-medium text-red-800">
                                                            Last Error
                                                        </h3>
                                                        <div className="mt-2 text-sm text-red-700">
                                                            {schedule.statistics.lastError}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex flex-col space-y-2 ml-4">
                                        <button
                                            onClick={() => handlePauseResume(schedule)}
                                            disabled={pauseMutation.isLoading || resumeMutation.isLoading}
                                            className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded ${
                                                schedule.isPaused
                                                    ? 'text-green-700 bg-green-100 hover:bg-green-200'
                                                    : 'text-yellow-700 bg-yellow-100 hover:bg-yellow-200'
                                            } disabled:opacity-50`}
                                        >
                                            {schedule.isPaused ? (
                                                <>
                                                    <PlayIcon className="h-3 w-3 mr-1" />
                                                    Resume
                                                </>
                                            ) : (
                                                <>
                                                    <PauseIcon className="h-3 w-3 mr-1" />
                                                    Pause
                                                </>
                                            )}
                                        </button>

                                        <button
                                            onClick={() => handleEdit(schedule)}
                                            className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                                        >
                                            <PencilIcon className="h-3 w-3 mr-1" />
                                            Edit
                                        </button>

                                        <button
                                            onClick={() => handleTrigger(schedule)}
                                            disabled={triggerMutation.isLoading}
                                            className="inline-flex items-center px-3 py-1 border border-blue-300 text-xs font-medium rounded text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                                        >
                                            <Cog6ToothIcon className="h-3 w-3 mr-1" />
                                            Test Run
                                        </button>

                                        <button
                                            onClick={() => handleDelete(schedule)}
                                            disabled={deleteMutation.isLoading}
                                            className="inline-flex items-center px-3 py-1 border border-red-300 text-xs font-medium rounded text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                                        >
                                            <TrashIcon className="h-3 w-3 mr-1" />
                                            Delete
                                        </button>
                                    </div>
                                </div>

                                {/* Statistics Bar */}
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <div className="flex items-center space-x-6 text-sm text-gray-600">
                                        <div className="flex items-center">
                                            <ChartBarIcon className="h-4 w-4 mr-1" />
                                            <span>
                                                {schedule.statistics.totalRuns} total runs
                                            </span>
                                        </div>
                                        <div className="flex items-center">
                                            <CheckCircleIcon className="h-4 w-4 mr-1 text-green-500" />
                                            <span>
                                                {schedule.statistics.successfulRuns} successful
                                            </span>
                                        </div>
                                        <div className="flex items-center">
                                            <ExclamationTriangleIcon className="h-4 w-4 mr-1 text-red-500" />
                                            <span>
                                                {schedule.statistics.failedRuns} failed
                                            </span>
                                        </div>
                                        {schedule.statistics.lastRun && (
                                            <div className="flex items-center">
                                                <CalendarIcon className="h-4 w-4 mr-1" />
                                                <span>
                                                    Last run: {new Date(schedule.statistics.lastRun).toLocaleString()}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Schedule Form Modal */}
            {showCreateForm && (
                <ScheduleForm
                    schedule={editingSchedule}
                    onClose={handleFormClose}
                    onSuccess={() => {
                        queryClient.invalidateQueries('schedules');
                        handleFormClose();
                    }}
                />
            )}

            {/* Help Section */}
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <span className="text-green-400 text-lg">✨</span>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">How Scheduling Works</h3>
                        <div className="mt-2 text-sm text-green-700">
                            <ul className="list-disc list-inside space-y-1">
                                <li><strong>Automated Generation:</strong> Schedules automatically generate posts based on your settings</li>
                                <li><strong>Content Selection:</strong> Uses your cached content sources for inspiration</li>
                                <li><strong>Manual Approval:</strong> Generated posts require your approval before publishing</li>
                                <li><strong>Platform Targeting:</strong> Choose which platforms to generate content for</li>
                                <li><strong>Quota Management:</strong> Respects your plan's monthly posting limits</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SchedulingTab;